import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, IconButton, InputAdornment, Menu, MenuItem,
  Slide, TextField, Typography, ThemeProvider, createTheme, CssBaseline,
  useMediaQuery, alpha,
} from '@mui/material';
import {
  Close as CloseIcon, Search as SearchIcon, ArrowBack as BackIcon,
  SwapVert as SortIcon, OpenInNew as OpenInNewIcon,
  BookmarkBorder as ShortlistOutlineIcon, Bookmark as ShortlistFilledIcon,
} from '@mui/icons-material';
import type { TransitionProps } from '@mui/material/transitions';
import type { WineListRender, WineListRenderItem, WineListRenderSection } from '../../types/venue';
import {
  sortWineListItems,
  WINE_LIST_SORT_LABELS,
  type WineListSortMode,
} from '../../utils/wineListSort';
import { buildItemSearchText, matchesQuery, scoreWineListItem } from '../../utils/wineListSearch';
import config from '../../config/environments';
import { useShortlist } from '../../utils/shortlist';
import ShortlistDialog from '../../components/m/ShortlistDialog';

/**
 * UX validation prototype for the customer-facing wine list.
 * Self-contained: no API, no auth, no real data.
 *
 * Lives at /m/preview-mock as a public route. The data shape mirrors the
 * existing /admin/.../render endpoint (WineListRender) so the production
 * /m/:slug route can drop this layout in unchanged once that endpoint exists.
 *
 * Design intent:
 *  - Restaurant lighting → dark mode forced for the prototype
 *  - Typography-first (serif for wine names) — feels like a real list
 *  - iPad: 2 columns past 900px; iPhone single column
 *  - Sticky list header + sticky section chip strip
 *  - Bottom-sheet detail on phone, centered dialog on tablet+
 */

// ── SAMPLE DATA ───────────────────────────────────────────────────────────────
// Shape matches WineListRender so this component can be repointed at a real
// public render endpoint without changes to the JSX below.

const MOCK_DATA: WineListRender = {
  list: {
    id: 'mock-list',
    name: 'Cellar Selection',
    type: 'FULL',
    status: 'ACTIVE',
    description: 'Sommelier picks · Updated weekly',
    venueName: 'Maison · Wine Bar',
    // No mock logo URL — the prototype demonstrates the text-fallback path.
    // Set this to e.g. "/some-logo.png" to preview the logo treatment.
    venueLogoUrl: null,
  },
  sections: [
    {
      id: 'sec-glass',
      name: 'By the Glass',
      description: 'Open now — poured tonight',
      position: 0,
      visibility: 'LIVE',
      items: [
        {
          id: 'g1',
          name: 'Riesling Trocken',
          producer: 'Keller',
          vintage: '2022',
          region: 'Rheinhessen',
          country: 'Germany',
          regionPath: ['Rheinhessen'],
          regionDisplay: 'Rheinhessen',
          color: 'WHITE',
          bottleSize: 750,
          bottlePrice: 95,
          price: 95,
          formats: [
            { name: 'Glass', volumeMl: 150, price: 22 },
            { name: 'Half', volumeMl: 75, price: 12 },
          ],
          currency: 'EUR',
          notes: 'Slate-driven, taut and saline. A benchmark dry Riesling from one of Germany\'s most precise estates.',
          bottlesAvailable: 7,
        },
        {
          id: 'g2',
          name: 'Chablis 1er Cru "Vaillons"',
          producer: 'William Fèvre',
          vintage: '2021',
          region: 'Chablis',
          country: 'France',
          regionPath: ['Burgundy', 'Chablis'],
          regionDisplay: 'Burgundy · Chablis',
          color: 'WHITE',
          bottleSize: 750,
          bottlePrice: 110,
          price: 110,
          formats: [
            { name: 'Glass', volumeMl: 150, price: 26 },
          ],
          currency: 'EUR',
          notes: 'Cool, mineral, perfectly classical Chablis. Showing beautifully now.',
          bottlesAvailable: 2,
        },
        {
          id: 'g3',
          name: 'Pinot Noir',
          producer: 'Felton Road',
          vintage: '2020',
          region: 'Central Otago',
          country: 'New Zealand',
          regionPath: ['Central Otago'],
          regionDisplay: 'Central Otago',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 140,
          price: 140,
          formats: [
            { name: 'Glass', volumeMl: 150, price: 32 },
            { name: 'Coravin', volumeMl: 60, price: 16 },
          ],
          currency: 'EUR',
          notes: 'Red cherry, wild thyme, fine tannin. From the Bannockburn block.',
          externalUrl: 'https://www.feltonroad.com/wines/pinot-noir-bannockburn',
          bottlesAvailable: 1,
        },
        {
          id: 'g4',
          name: 'Champagne Brut Réserve',
          producer: 'Jacques Selosse',
          vintage: 'NV',
          region: 'Champagne',
          country: 'France',
          regionPath: ['Champagne'],
          regionDisplay: 'Champagne',
          color: 'WHITE',
          bottleSize: 750,
          bottlePrice: 380,
          price: 380,
          formats: [
            { name: 'Coravin', volumeMl: 100, price: 58 },
          ],
          currency: 'EUR',
          notes: 'Vinous, oxidative, profound. Drink slowly.',
          bottlesAvailable: 0,
        },
        {
          id: 'g5',
          name: 'Bandol Rosé',
          producer: 'Domaine Tempier',
          vintage: '2023',
          region: 'Provence',
          country: 'France',
          regionPath: ['Provence', 'Bandol'],
          regionDisplay: 'Provence · Bandol',
          color: 'PINK_ROSE',
          bottleSize: 750,
          bottlePrice: 88,
          price: 88,
          formats: [
            { name: 'Glass', volumeMl: 150, price: 18 },
            { name: 'Half', volumeMl: 75, price: 10 },
          ],
          currency: 'EUR',
          notes: 'Mourvèdre-led, structured, savoury. Drinks like a serious red.',
          bottlesAvailable: 4,
        },
      ],
    },
    {
      id: 'sec-burgundy',
      name: 'Burgundy',
      description: 'Côte d\'Or, Côte Chalonnaise, and beyond',
      position: 1,
      visibility: 'LIVE',
      items: [
        {
          id: 'b1',
          name: 'Bourgogne Rouge',
          producer: 'Sylvain Pataille',
          vintage: '2021',
          region: 'Marsannay',
          country: 'France',
          regionPath: ['Burgundy', 'Côte de Nuits', 'Marsannay'],
          regionDisplay: 'Burgundy · Côte de Nuits · Marsannay',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 95,
          price: 95,
          formats: [],
          currency: 'EUR',
          notes: 'Pure Pinot, transparent and lifted. From an iconic Marsannay vigneron.',
          bottlesAvailable: 6,
        },
        {
          id: 'b2',
          name: 'Volnay 1er Cru "Caillerets"',
          producer: 'Hubert de Montille',
          vintage: '2018',
          region: 'Volnay',
          country: 'France',
          regionPath: ['Burgundy', 'Côte de Beaune', 'Volnay'],
          regionDisplay: 'Burgundy · Côte de Beaune · Volnay',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 280,
          price: 280,
          formats: [],
          currency: 'EUR',
          notes: 'Rose petal and crushed strawberry over a fine, chalky frame.',
          bottlesAvailable: 3,
        },
        {
          id: 'b3',
          name: 'Meursault "Les Tillets"',
          producer: 'Jean-Marc Roulot',
          vintage: '2020',
          region: 'Meursault',
          country: 'France',
          regionPath: ['Burgundy', 'Côte de Beaune', 'Meursault'],
          regionDisplay: 'Burgundy · Côte de Beaune · Meursault',
          color: 'WHITE',
          bottleSize: 750,
          bottlePrice: 320,
          price: 320,
          formats: [],
          currency: 'EUR',
          notes: 'Tense, hazelnut, oyster shell. A high-altitude village cuvée from the master.',
          externalUrl: 'https://www.domaine-roulot.fr/en/wines',
          scores: [
            { source: 'WA', score: 96 },
            { source: 'JS', score: 95 },
          ],
          bottlesAvailable: 2,
        },
        {
          id: 'b4',
          name: 'Chambolle-Musigny "Les Charmes"',
          producer: 'Georges Roumier',
          vintage: '2019',
          region: 'Chambolle-Musigny',
          country: 'France',
          regionPath: ['Burgundy', 'Côte de Nuits', 'Chambolle-Musigny'],
          regionDisplay: 'Burgundy · Côte de Nuits · Chambolle-Musigny',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 950,
          price: 950,
          formats: [],
          currency: 'EUR',
          notes: 'Silken texture, layered red and dark fruit, gravelly tension.',
          externalUrl: 'https://www.domaine-roumier.com/en/our-wines',
          scores: [
            { source: 'JS', score: 97 },
            { source: 'WA', score: 96 },
            { source: 'WS', score: 95 },
          ],
          bottlesAvailable: 1,
        },
        {
          id: 'b5',
          name: 'Corton-Charlemagne Grand Cru',
          producer: 'Bonneau du Martray',
          vintage: '2017',
          region: 'Corton',
          country: 'France',
          regionPath: ['Burgundy', 'Côte de Beaune', 'Corton'],
          regionDisplay: 'Burgundy · Côte de Beaune · Corton',
          color: 'WHITE',
          bottleSize: 750,
          bottlePrice: 540,
          price: 540,
          formats: [],
          currency: 'EUR',
          notes: 'Stone-fruit and crushed limestone. Drinking beautifully right now.',
          bottlesAvailable: 0,
        },
      ],
    },
    {
      id: 'sec-newworld',
      name: 'New World Reds',
      description: 'California, Oregon, Australia, Argentina',
      position: 2,
      visibility: 'LIVE',
      items: [
        {
          id: 'n1',
          name: 'Cabernet Sauvignon "To Kalon"',
          producer: 'Schrader',
          vintage: '2019',
          region: 'Napa Valley',
          country: 'United States',
          regionPath: ['California', 'Napa Valley'],
          regionDisplay: 'California · Napa Valley',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 720,
          price: 720,
          formats: [],
          currency: 'EUR',
          notes: 'Cassis, bay leaf, espresso. Built for the long haul.',
          externalUrl: 'https://www.schraderwines.com/our-wines/to-kalon',
          scores: [
            { source: 'WS', score: 98 },
            { source: 'AV', score: 96 },
          ],
          bottlesAvailable: 2,
        },
        {
          id: 'n2',
          name: 'Syrah "Reynvaan In the Rocks"',
          producer: 'Reynvaan Family Vineyards',
          vintage: '2020',
          region: 'Walla Walla',
          country: 'United States',
          regionPath: ['Washington', 'Walla Walla'],
          regionDisplay: 'Washington · Walla Walla',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 245,
          price: 245,
          formats: [],
          currency: 'EUR',
          notes: 'Smoke, olive, blue fruit. Northern Rhône energy from Washington.',
          bottlesAvailable: 7,
        },
        {
          id: 'n3',
          name: 'Pinot Noir "Eola-Amity Hills"',
          producer: 'Cristom',
          vintage: '2021',
          region: 'Willamette Valley',
          country: 'United States',
          regionPath: ['Oregon', 'Willamette Valley'],
          regionDisplay: 'Oregon · Willamette Valley',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 145,
          price: 145,
          formats: [],
          currency: 'EUR',
          notes: 'Cranberry, sous bois, rose hip. Cool, structured Oregon Pinot.',
          bottlesAvailable: 5,
        },
        {
          id: 'n4',
          name: 'Malbec "Adrianna Vineyard"',
          producer: 'Catena Zapata',
          vintage: '2018',
          region: 'Mendoza',
          country: 'Argentina',
          regionPath: ['Mendoza'],
          regionDisplay: 'Mendoza',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 185,
          price: 185,
          formats: [],
          currency: 'EUR',
          notes: 'High-altitude Malbec — violet, graphite, cool dark berry.',
          bottlesAvailable: 1,
        },
        {
          id: 'n5',
          name: 'Shiraz "RunRig"',
          producer: 'Torbreck',
          vintage: '2017',
          region: 'Barossa Valley',
          country: 'Australia',
          regionPath: ['South Australia', 'Barossa Valley'],
          regionDisplay: 'South Australia · Barossa Valley',
          color: 'RED',
          bottleSize: 750,
          bottlePrice: 410,
          price: 410,
          formats: [],
          currency: 'EUR',
          notes: 'Plummy, savoury, full-bodied. A modern Australian classic.',
          bottlesAvailable: 0,
        },
      ],
    },
  ],
};

// ── THEME ─────────────────────────────────────────────────────────────────────
// Forced-dark theme tuned for restaurant lighting. Premium, low-contrast,
// typography-led. Serif stack for wine names; system sans for everything else.

const SERIF = '"Cormorant Garamond", "EB Garamond", Georgia, "Times New Roman", serif';

export const previewTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0e0d0c',
      paper: '#161513',
    },
    primary: {
      main: '#c9a45a', // warm gold accent for active chips, links
    },
    text: {
      primary: '#f1ece3',
      secondary: '#9a9286',
      disabled: '#5b5650',
    },
    divider: '#262420',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

// ── HELPERS ───────────────────────────────────────────────────────────────────

/**
 * W9B — color dot palette. Picked for clear differentiation against the
 * dark restaurant background — each category reads as visibly distinct
 * even at the small dot size used inline with the region text.
 *
 * Unknown / null colors fall back to neutral grey so the dot is always
 * present and the row alignment doesn't shift.
 */
const COLOR_DOT: Record<string, string> = {
  SPARKLING: '#4caf6d', // clean green
  WHITE:     '#f0c43d', // bright yellow / gold
  ROSE:      '#e07ba3', // pink
  PINK_ROSE: '#e07ba3', // alias used by current data
  RED:       '#d13447', // saturated red
  SWEET:     '#e88533', // orange / amber
  FORTIFIED: '#9b6cc4', // purple
  ORANGE:    '#e88533', // orange wine — same family as sweet
};
const NEUTRAL_DOT = '#8a8a8a'; // unknown / not set
const colorDotFor = (color: string | null | undefined): string =>
  (color && COLOR_DOT[color]) || NEUTRAL_DOT;

/**
 * Format the integer-rounded price amount only. Currency is rendered as a
 * separate, smaller span by the price-row components — see W10F.
 */
const formatPriceAmount = (amount: number | null | undefined): string | null => {
  if (amount == null) return null;
  return amount.toFixed(0);
};

/**
 * Order scores by the canonical critic order (JS, WA, WS, AV) so badges
 * render in a stable sequence regardless of how they were stored. Filter
 * out anything malformed defensively.
 */
const SCORE_ORDER: Record<string, number> = { JS: 0, WA: 1, WS: 2, AV: 3 };
const orderedScores = (item: WineListRenderItem): Array<{ source: string; score: number }> => {
  const scores = item.scores;
  if (!Array.isArray(scores) || scores.length === 0) return [];
  return [...scores]
    .filter(s => s && typeof s.score === 'number' && typeof s.source === 'string')
    .sort((a, b) => (SCORE_ORDER[a.source] ?? 99) - (SCORE_ORDER[b.source] ?? 99));
};

/**
 * Resolve a logo URL to an absolute string the browser can fetch.
 *
 * The public render endpoint emits venueLogoUrl as a path like
 * `/api/public/venue/:id/logo/file`. Browsers resolve `<img src="/api/...">`
 * relative to the *page* host (the web bundle), not the API host. In any
 * deployment where the web bundle and the API live on different hosts (which
 * is true for every environment we have — localhost:3000 vs localhost:9000,
 * notawine.com vs api.notawine.com), the image then 404s on the wrong host.
 *
 * Fix: when the URL starts with `/api`, prefix with the API origin derived
 * from config.apiUrl (which already ends in `/api`). Absolute URLs (http/https)
 * pass through unchanged so a future hosted-image flow keeps working.
 */
const resolveLogoUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // config.apiUrl already includes /api; strip the trailing /api to get the
  // bare origin and re-attach the path so we don't double-prefix.
  const apiOrigin = config.apiUrl.replace(/\/api\/?$/, '');
  return url.startsWith('/') ? `${apiOrigin}${url}` : `${apiOrigin}/${url}`;
};

const availabilityText = (n: number | null | undefined): string | null => {
  if (n == null) return null;
  if (n === 0) return 'Out of stock';
  if (n === 1) return '1 bottle';
  return `${n} bottles`;
};

/**
 * W10B — friendly label for a bottle size in millilitres. Used as the
 * primary label in the price block instead of a hard-coded "Bottle".
 *
 *   375  → "Half Bottle"
 *   750  → "Bottle"
 *   1500 → "Magnum"
 *   <1000 (other) → "{n} ml bottle"
 *   ≥1000 (other) → "{liters} L bottle"   (trailing zeros stripped)
 *   missing/invalid → null
 */
const formatBottleSizeLabel = (sizeMl: number | null | undefined): string | null => {
  if (sizeMl == null || !isFinite(sizeMl) || sizeMl <= 0) return null;
  if (sizeMl === 375) return 'Half Bottle';
  if (sizeMl === 750) return 'Bottle';
  if (sizeMl === 1500) return 'Magnum';
  if (sizeMl < 1000) return `${sizeMl} ml bottle`;
  const liters = sizeMl / 1000;
  // Trim trailing zeros so 1.5 stays "1.5" and 3 stays "3", not "3.00".
  const litersStr = liters % 1 === 0
    ? String(liters)
    : liters.toFixed(2).replace(/\.?0+$/, '');
  return `${litersStr} L bottle`;
};

/**
 * W10E — pure-numeric size for the LEFT side of a wine card (under the wine
 * name). Always millilitres, no smart wording. The right-side price block
 * keeps `formatBottleSizeLabel` so guests still see "Bottle / Half Bottle /
 * Magnum / 3 L bottle" in the price column.
 *
 *   375  → "375 ml"
 *   750  → "750 ml"
 *   1500 → "1500 ml"
 *   3000 → "3000 ml"
 *   missing/invalid → null
 */
const formatBottleSizeNumeric = (sizeMl: number | null | undefined): string | null => {
  if (sizeMl == null || !isFinite(sizeMl) || sizeMl <= 0) return null;
  return `${sizeMl} ml`;
};

// ── DETAIL MODAL TRANSITIONS ──────────────────────────────────────────────────

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ── WINE CARD ─────────────────────────────────────────────────────────────────

interface WineCardProps {
  item: WineListRenderItem;
  onClick: () => void;
  /** W10 — true when this wine is in the guest's shortlist. */
  isShortlisted: boolean;
  /** W10 — toggle handler; stop event bubbling is owned by the button. */
  onToggleShortlist: (itemId: string) => void;
}

const WineCard: React.FC<WineCardProps> = ({ item, onClick, isShortlisted, onToggleShortlist }) => {
  // W9 — always render a dot (neutral grey when color is missing) so cards align.
  const colorDot = colorDotFor(item.color);
  const availability = availabilityText(item.bottlesAvailable);
  const isOut = item.bottlesAvailable === 0;
  const isLow = item.bottlesAvailable != null && item.bottlesAvailable > 0 && item.bottlesAvailable <= 3;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        py: { xs: 2.5, md: 3 },
        px: { xs: 0, md: 1.5 },
        cursor: 'pointer',
        display: 'flex',
        gap: { xs: 2, md: 3.5 },
        alignItems: 'flex-start',
        opacity: isOut ? 0.55 : 1,
        transition: 'opacity .15s ease, background-color .15s ease',
        position: 'relative', // anchor for the absolute-positioned heart toggle
        '&:hover, &:active': {
          backgroundColor: alpha('#fff', 0.015),
        },
        '&:focus-visible': {
          outline: '2px solid #c9a45a',
          outlineOffset: 2,
          borderRadius: 4,
        },
      }}
    >
      {/* W10 — shortlist toggle (bookmark). Absolutely positioned in the
          top-right so it doesn't disturb the existing two-column layout.
          Stops propagation so tapping the bookmark never opens the wine
          detail modal. */}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleShortlist(item.id);
        }}
        aria-label={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
        aria-pressed={isShortlisted}
        sx={{
          position: 'absolute',
          top: { xs: 8, md: 12 },
          right: { xs: -4, md: 0 },
          color: isShortlisted ? 'primary.main' : 'text.secondary',
          p: 0.75,
          transition: 'color .15s ease, transform .15s ease',
          '&:hover': {
            color: 'primary.main',
            backgroundColor: 'transparent',
            transform: 'scale(1.08)',
          },
        }}
      >
        {isShortlisted ? <ShortlistFilledIcon fontSize="small" /> : <ShortlistOutlineIcon fontSize="small" />}
      </IconButton>
      {/* Left column: producer → name + vintage → region → availability */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Producer — small caps; refined (lighter weight + slightly tighter
            tracking) so it sits above the wine name without competing with
            the serif anchor. Em-dash placeholder when missing. */}
        <Typography
          sx={{
            fontSize: { xs: '0.7rem', md: '0.74rem' },
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {item.producer ?? '—'}
        </Typography>

        {/* Wine name — serif, the card's visual anchor.
            Vintage stays inline (clearly attached) but visually softer:
            italic, slightly smaller, secondary colour. */}
        <Typography
          sx={{
            fontFamily: SERIF,
            fontSize: { xs: '1.32rem', md: '1.5rem' },
            lineHeight: 1.18,
            color: 'text.primary',
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          {item.name}
          {/* W10B — vintage is intentionally NOT rendered here; the wine
              snapshot's `name` already includes the vintage on the existing
              wines. The line below shows the numeric bottle size. */}
        </Typography>
        {(() => {
          // W10E — left side is pure numeric ml (e.g. "750 ml"); the smart
          // wording ("Bottle", "Half Bottle", "Magnum", "3 L bottle") lives
          // exclusively in the price block on the right.
          const sizeLabel = formatBottleSizeNumeric(item.bottleSize);
          if (!sizeLabel) return null;
          return (
            <Typography
              sx={{
                mt: 0.15,
                fontSize: { xs: '0.78rem', md: '0.82rem' },
                color: 'text.secondary',
                letterSpacing: '0.01em',
              }}
            >
              {sizeLabel}
            </Typography>
          );
        })()}

        {/* Region row — sans serif, no italic (italic is reserved for vintage so
            the two don't compete). Color dot sits inline at the same baseline.
            Prefers regionDisplay (full hierarchy "Burgundy · Côte de Beaune ·
            Meursault") when present, falls back to the snapshot region name. */}
        {(() => {
          const regionLabel = item.regionDisplay ?? item.region;
          // W9 — colorDot is now always set (neutral grey when color is missing).
          // The row anchors on regionLabel so we don't render a floating dot
          // with no accompanying text.
          if (!regionLabel) return null;
          return (
            <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* W9B — slightly larger dot (9px) for better legibility on
                  dark restaurant lighting; clear differentiation per color. */}
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  backgroundColor: colorDot,
                  flexShrink: 0,
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.25) inset',
                }}
              />
              {regionLabel && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.83rem', md: '0.86rem' },
                    letterSpacing: '0.005em',
                  }}
                >
                  {regionLabel}
                </Typography>
              )}
            </Box>
          );
        })()}

        {/* Availability + scores — same row, both subtle.
            Scores render as "JS96", "WA95", etc. in canonical order JS→WA→WS→AV.
            All elements skip-render when absent so the row collapses cleanly. */}
        {(() => {
          const scores = orderedScores(item);
          if (!availability && scores.length === 0) return null;
          return (
            <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              {availability && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    fontSize: '0.7rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    color: isOut ? 'text.disabled' : isLow ? '#c79956' : 'text.secondary',
                  }}
                >
                  {availability}
                </Box>
              )}
              {scores.map(s => (
                <Box
                  key={s.source}
                  component="span"
                  sx={{
                    display: 'inline-block',
                    fontSize: '0.7rem',
                    letterSpacing: '0.04em',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.75,
                    px: 0.6,
                    py: 0.1,
                  }}
                  aria-label={`Score ${s.score} from ${s.source}`}
                >
                  {s.source}{Math.round(s.score)}
                </Box>
              ))}
            </Box>
          );
        })()}
      </Box>

      {/* Right column: price block (bottle first, then formats).
          W10B — `pr` reserves horizontal space for the absolutely-positioned
          bookmark icon at the card's top-right so the bottle-size label
          ("Magnum", "Half Bottle", etc.) doesn't visually merge with it. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1.5,
          minWidth: { xs: 92, md: 116 },
          pr: { xs: 4, md: 4.5 },
        }}
      >
        {item.bottlePrice != null && (
          <PriceRow
            label={formatBottleSizeLabel(item.bottleSize) ?? 'Bottle'}
            volume={null}
            amount={item.bottlePrice}
            currency={item.currency}
            primary
          />
        )}
        {/* W9B correction — removed the 36px gold hairline that used to sit
            between Bottle and pour formats; users were reading it as a
            stray "dash above the wine name". The Bottle/format labels and
            the size step-down already differentiate the two groups. */}
        {item.formats?.map(fmt => (
          <PriceRow
            key={`${fmt.name}-${fmt.volumeMl}`}
            label={fmt.name}
            volume={`${fmt.volumeMl} ml`}
            amount={fmt.price}
            currency={item.currency}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── PRICE ROW ─────────────────────────────────────────────────────────────────

/**
 * Price row used inside a WineCard's right column.
 *
 * Sentence-case italic serif label (e.g. "Bottle", "Glass · 150 ml") sits
 * above a serif price. Avoids the uppercase + letterspacing combo that read
 * as admin-table-like. Bottle (`primary`) is heavier and larger to anchor
 * the block; pour formats sit lighter beneath the divider.
 */
const PriceRow: React.FC<{
  label: string;
  volume: string | null;
  amount: number | null | undefined;
  currency: string | null | undefined;
  primary?: boolean;
}> = ({ label, volume, amount, currency, primary }) => {
  const amountStr = formatPriceAmount(amount);
  if (!amountStr) return null;
  return (
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontSize: primary ? '0.78rem' : '0.74rem',
          color: 'text.secondary',
          fontWeight: 400,
          lineHeight: 1.2,
          mb: 0.25,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {volume && (
          <Box component="span" sx={{ ml: 0.5, color: 'text.disabled' }}>
            · {volume}
          </Box>
        )}
      </Typography>
      <Typography
        sx={{
          fontFamily: SERIF,
          fontSize: primary ? { xs: '1.25rem', md: '1.4rem' } : { xs: '0.98rem', md: '1.05rem' },
          fontWeight: primary ? 600 : 500,
          color: 'text.primary',
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
          letterSpacing: primary ? '-0.005em' : 0,
        }}
      >
        {/* W10F — currency rendered smaller + muted so the price number stays
            the visual anchor (e.g. HKD 1550 — "HKD" reads as quiet metadata
            attached to the amount). Aligned on the baseline of the amount. */}
        {currency && (
          <Box
            component="span"
            sx={{
              fontSize: '0.65em',
              fontWeight: 500,
              color: 'text.secondary',
              letterSpacing: '0.04em',
              mr: 0.5,
            }}
          >
            {currency}
          </Box>
        )}
        {amountStr}
      </Typography>
    </Box>
  );
};

// ── DETAIL MODAL ──────────────────────────────────────────────────────────────

interface DetailProps {
  item: WineListRenderItem | null;
  onClose: () => void;
  fullScreen: boolean;
}

const WineDetail: React.FC<DetailProps> = ({ item, onClose, fullScreen }) => {
  const colorDot = colorDotFor(item?.color);
  const availability = item ? availabilityText(item.bottlesAvailable) : null;
  const isOut = item?.bottlesAvailable === 0;
  const isLow = item?.bottlesAvailable != null && item.bottlesAvailable > 0 && item.bottlesAvailable <= 3;

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      fullScreen={fullScreen}
      TransitionComponent={fullScreen ? SlideUp : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
          color: 'text.primary',
          borderRadius: fullScreen ? '20px 20px 0 0' : 3,
          ...(fullScreen ? { mt: 4 } : {}),
        },
      }}
    >
      {item && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Top bar with close — flush with content for breathability */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2.5, py: 1.5,
          }}>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label={fullScreen ? 'Back to list' : 'Close'}
              sx={{ color: 'text.secondary' }}
            >
              {fullScreen ? <BackIcon /> : <CloseIcon />}
            </IconButton>
            {(() => {
              const detailScores = item ? orderedScores(item) : [];
              if (!availability && detailScores.length === 0) return null;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {detailScores.map(s => (
                    <Box
                      key={s.source}
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        letterSpacing: '0.04em',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 0.75,
                        px: 0.65,
                        py: 0.15,
                      }}
                      aria-label={`Score ${s.score} from ${s.source}`}
                    >
                      {s.source}{Math.round(s.score)}
                    </Box>
                  ))}
                  {availability && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: isOut ? 'text.disabled' : isLow ? '#d4a04a' : 'text.secondary',
                      }}
                    >
                      {availability}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>

          {/* Body */}
          <Box sx={{ px: { xs: 3, md: 4 }, pb: 4, flex: 1, overflowY: 'auto' }}>
            {/* Producer */}
            <Typography
              sx={{
                fontSize: '0.78rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontWeight: 600,
                mb: 1,
              }}
            >
              {item.producer ?? '—'}
            </Typography>

            {/* Wine name */}
            <Typography
              sx={{
                fontFamily: SERIF,
                fontSize: { xs: '2rem', md: '2.4rem' },
                lineHeight: 1.1,
                color: 'text.primary',
                fontWeight: 500,
                letterSpacing: '-0.015em',
              }}
            >
              {item.name}
            </Typography>
            {/* W10B — vintage intentionally omitted: the snapshot `name`
                already contains it on existing wines, so a separate render
                would duplicate the year. */}

            {/* Country (small caption above) + region hierarchy + color + size.
                Country gets its own small line so the user immediately sees the
                origin without parsing the full hierarchy string. */}
            {item.country && (
              <Typography
                sx={{
                  mt: 2,
                  fontSize: '0.72rem',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {item.country}
              </Typography>
            )}
            <Box sx={{ mt: item.country ? 0.5 : 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {/* W9 — dot always present (neutral grey for unknown).
                  W9B — bumped to 10px for legibility on the modal hero area. */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: colorDot,
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.25) inset',
                }}
              />
              {(item.regionDisplay ?? item.region) && (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {item.regionDisplay ?? item.region}
                </Typography>
              )}
              {(() => {
                // W10E follow-up — match the card's left-side numeric format
                // (e.g. "1500 ml", not "1.5L") so guests see the same unit
                // in both the card and the detail modal.
                const sizeNumeric = formatBottleSizeNumeric(item.bottleSize);
                if (!sizeNumeric) return null;
                return (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: 'text.disabled' }} />
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {sizeNumeric}
                    </Typography>
                  </>
                );
              })()}
            </Box>

            {/* Notes */}
            {item.notes && (
              <Typography
                sx={{
                  mt: 3,
                  fontFamily: SERIF,
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  fontStyle: 'italic',
                  lineHeight: 1.55,
                  color: 'text.primary',
                  letterSpacing: '0.005em',
                }}
              >
                "{item.notes}"
              </Typography>
            )}

            {/* Pricing block */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Pricing
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {item.bottlePrice != null && (
                  <DetailPriceRow
                    label={formatBottleSizeLabel(item.bottleSize) ?? 'Bottle'}
                    volume={item.bottleSize ? `${item.bottleSize} ml` : null}
                    amount={item.bottlePrice}
                    currency={item.currency}
                    primary
                  />
                )}
                {item.formats?.map(fmt => (
                  <DetailPriceRow
                    key={`${fmt.name}-${fmt.volumeMl}`}
                    label={fmt.name}
                    volume={`${fmt.volumeMl} ml`}
                    amount={fmt.price}
                    currency={item.currency}
                  />
                ))}
              </Box>
            </Box>

            {/* W7B — optional external link.
                Rendered as-is, no validation, no transformation. Hidden when
                missing. Opens in a new tab with rel="noopener noreferrer" to
                prevent the linked page from accessing window.opener. Visually
                a quiet metadata line — not a primary CTA. */}
            {item.externalUrl && (
              <Box sx={{ mt: 3 }}>
                <Box
                  component="a"
                  href={item.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'text.secondary',
                    fontSize: '0.82rem',
                    textDecoration: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 0.25,
                    transition: 'color .15s ease, border-color .15s ease',
                    '&:hover': {
                      color: 'text.primary',
                      borderColor: 'text.disabled',
                    },
                    '&:focus-visible': {
                      outline: '2px solid #c9a45a',
                      outlineOffset: 3,
                      borderRadius: 1,
                    },
                  }}
                >
                  View details
                  <OpenInNewIcon sx={{ fontSize: '0.95rem' }} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

const DetailPriceRow: React.FC<{
  label: string;
  volume: string | null;
  amount: number | null | undefined;
  currency: string | null | undefined;
  primary?: boolean;
}> = ({ label, volume, amount, currency, primary }) => {
  const amountStr = formatPriceAmount(amount);
  if (!amountStr) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
      <Box>
        <Typography
          sx={{
            fontFamily: SERIF,
            fontSize: primary ? '1.15rem' : '1rem',
            color: 'text.primary',
            fontWeight: primary ? 600 : 500,
          }}
        >
          {label}
        </Typography>
        {volume && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', letterSpacing: '0.05em' }}>
            {volume}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: SERIF,
          fontSize: primary ? '1.3rem' : '1.05rem',
          fontWeight: primary ? 600 : 500,
          color: 'text.primary',
          whiteSpace: 'nowrap',
        }}
      >
        {/* W10F — same currency-quieter-than-amount treatment as the card. */}
        {currency && (
          <Box
            component="span"
            sx={{
              fontSize: '0.65em',
              fontWeight: 500,
              color: 'text.secondary',
              letterSpacing: '0.04em',
              mr: 0.5,
            }}
          >
            {currency}
          </Box>
        )}
        {amountStr}
      </Typography>
    </Box>
  );
};

// ── RESPONSIVE SORT CONTROLS ──────────────────────────────────────────────────
// Two presentations of the same `sortMode` state:
//   - SortButton  (phone)   — single button + popover Menu listing all 7 options
//   - SortChipRow (tablet)  — chip-strip with direction toggle on repeat tap
// Both write to the same `WineListSortMode` and rely on the shared
// sortWineListItems util — no logic duplication.

type SortDimension = 'PRICE' | 'VINTAGE' | 'COLOR';
type SortDirection = 'ASC' | 'DESC';

const DIMENSION_LABEL: Record<SortDimension, string> = {
  PRICE: 'Price',
  VINTAGE: 'Vintage',
  COLOR: 'Color',
};

/**
 * The direction a dimension takes when first activated from a different mode.
 * Picked to match the most-likely-wanted-first behaviour on a wine list:
 *   - Price: cheapest first
 *   - Vintage: newest first
 *   - Color: white first (lighter → heavier reading flow)
 */
const DIMENSION_DEFAULT_DIRECTION: Record<SortDimension, SortDirection> = {
  PRICE: 'ASC',
  VINTAGE: 'DESC',
  COLOR: 'ASC',
};

const dimensionToMode = (dim: SortDimension, dir: SortDirection): WineListSortMode => {
  if (dim === 'PRICE') return dir === 'ASC' ? 'PRICE_ASC' : 'PRICE_DESC';
  if (dim === 'VINTAGE') return dir === 'ASC' ? 'VINTAGE_OLD_FIRST' : 'VINTAGE_YOUNG_FIRST';
  return dir === 'ASC' ? 'COLOR_WHITE_FIRST' : 'COLOR_RED_FIRST';
};

const modeToDimension = (
  mode: WineListSortMode,
): { dim: SortDimension; dir: SortDirection } | null => {
  switch (mode) {
    case 'PRICE_ASC': return { dim: 'PRICE', dir: 'ASC' };
    case 'PRICE_DESC': return { dim: 'PRICE', dir: 'DESC' };
    case 'VINTAGE_OLD_FIRST': return { dim: 'VINTAGE', dir: 'ASC' };
    case 'VINTAGE_YOUNG_FIRST': return { dim: 'VINTAGE', dir: 'DESC' };
    case 'COLOR_WHITE_FIRST': return { dim: 'COLOR', dir: 'ASC' };
    case 'COLOR_RED_FIRST': return { dim: 'COLOR', dir: 'DESC' };
    default: return null;
  }
};

const SortButton: React.FC<{
  value: WineListSortMode;
  onChange: (mode: WineListSortMode) => void;
}> = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<SortIcon sx={{ fontSize: 18 }} />}
        aria-label="Sort wines"
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          borderRadius: 99,
          height: 40,
          px: 1.75,
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'text.primary',
          backgroundColor: alpha('#fff', 0.025),
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: alpha('#fff', 0.04),
            borderColor: 'text.disabled',
          },
        }}
      >
        Sort
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mt: 0.5,
              minWidth: 220,
            },
          },
        }}
      >
        {(Object.keys(WINE_LIST_SORT_LABELS) as WineListSortMode[]).map(mode => (
          <MenuItem
            key={mode}
            selected={mode === value}
            onClick={() => { onChange(mode); setAnchor(null); }}
            sx={{
              fontSize: '0.9rem',
              py: 1.25,
              '&.Mui-selected': {
                backgroundColor: alpha('#c9a45a', 0.12),
                color: 'primary.main',
                '&:hover': { backgroundColor: alpha('#c9a45a', 0.18) },
              },
            }}
          >
            {WINE_LIST_SORT_LABELS[mode]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SortChipRow: React.FC<{
  value: WineListSortMode;
  onChange: (mode: WineListSortMode) => void;
}> = ({ value, onChange }) => {
  const current = modeToDimension(value);

  /** Tap a dimension chip:
   *   - inactive → activate with the dimension's default direction
   *   - active   → toggle direction (ASC ⇄ DESC)
   */
  const handleDimensionTap = (dim: SortDimension) => {
    if (current?.dim === dim) {
      onChange(dimensionToMode(dim, current.dir === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      onChange(dimensionToMode(dim, DIMENSION_DEFAULT_DIRECTION[dim]));
    }
  };

  const chipBase = {
    flexShrink: 0,
    height: 32,
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    borderRadius: 99,
  } as const;

  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'text.secondary',
          mr: 0.5,
        }}
      >
        Sort
      </Typography>

      <Chip
        label="Default"
        size="small"
        onClick={() => onChange('DEFAULT')}
        variant={value === 'DEFAULT' ? 'filled' : 'outlined'}
        color={value === 'DEFAULT' ? 'primary' : 'default'}
        sx={{
          ...chipBase,
          ...(value === 'DEFAULT' ? {} : { color: 'text.secondary', borderColor: 'divider' }),
        }}
      />

      {(['PRICE', 'VINTAGE', 'COLOR'] as SortDimension[]).map(dim => {
        const active = current?.dim === dim;
        const arrow = active ? (current!.dir === 'ASC' ? ' ↑' : ' ↓') : '';
        return (
          <Chip
            key={dim}
            label={`${DIMENSION_LABEL[dim]}${arrow}`}
            size="small"
            onClick={() => handleDimensionTap(dim)}
            variant={active ? 'filled' : 'outlined'}
            color={active ? 'primary' : 'default'}
            aria-label={
              active
                ? `Sort by ${DIMENSION_LABEL[dim]} (${current!.dir === 'ASC' ? 'ascending' : 'descending'}). Tap to flip direction.`
                : `Sort by ${DIMENSION_LABEL[dim]}`
            }
            sx={{
              ...chipBase,
              ...(active ? {} : { color: 'text.secondary', borderColor: 'divider' }),
            }}
          />
        );
      })}
    </Box>
  );
};

// ── MAIN MENU VIEW ────────────────────────────────────────────────────────────
// Reusable wine list rendering. Used by:
//   - /m/preview-mock (PreviewMock wrapper, MOCK_DATA)
//   - /m/:slug       (PublicWineList wrapper, fetched data)
// Takes `data: WineListRender` so the consumer owns data fetching.

interface WineListMenuViewProps {
  data: WineListRender;
}

export const WineListMenuView: React.FC<WineListMenuViewProps> = ({ data }) => {
  const isTablet = useMediaQuery('(min-width: 900px)');
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<WineListSortMode>('DEFAULT');
  const [detailItem, setDetailItem] = useState<WineListRenderItem | null>(null);
  // Track logo-load failure so onError doesn't just hide the <img> — we flip
  // to the venue-name fallback caption instead. Reset whenever the source
  // URL changes (e.g. logo replaced server-side, page reload).
  const [logoFailed, setLogoFailed] = useState(false);
  // W9D — memoise the resolved URL so the dep array below is stable, and
  // append a per-mount cache-bust query param. Without the cache-bust, a
  // browser that cached a 404 response from before the venue uploaded its
  // logo would keep serving that 404 to <img> on every subsequent page
  // load, permanently flipping the header to the venue-name fallback.
  const resolvedLogoSrc = useMemo(() => {
    const base = resolveLogoUrl(data.list.venueLogoUrl);
    if (!base) return null;
    // Per-mount stamp: fresh on every page load, stable within the session
    // so the browser can still cache the image after the first request.
    return `${base}${base.includes('?') ? '&' : '?'}v=${Date.now()}`;
  }, [data.list.venueLogoUrl]);
  useEffect(() => { setLogoFailed(false); }, [resolvedLogoSrc]);

  // W10 — guest shortlist. Storage key is `shortlist:<slug>`; for the mock
  // route (no slug) we fall back to "preview-mock" so the prototype works
  // identically to a real list. State + cross-tab sync owned by useShortlist.
  const shortlistSlug = data.list.slug ?? 'preview-mock';
  const shortlist = useShortlist(shortlistSlug);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  // Items currently in the shortlist, in insertion order, resolved against
  // the data we already have. Items the guest hearted then no longer
  // present in the list (e.g. taken off the menu) silently drop out.
  const shortlistItems = useMemo<WineListRenderItem[]>(() => {
    const byId = new Map<string, WineListRenderItem>();
    for (const s of data.sections) for (const item of s.items) byId.set(item.id, item);
    return shortlist.ids
      .map(id => byId.get(id))
      .filter((x): x is WineListRenderItem => Boolean(x));
  }, [shortlist.ids, data.sections]);

  // Refs for section nav scrolling
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const visibleSections = useMemo<WineListRenderSection[]>(() => {
    const q = search.trim();
    // W9 — search always runs across the full list. The section chip filter
    // applies only when search is empty, so a guest can type "pio cesare"
    // while Riesling is selected and still find Pio Cesare wines elsewhere.
    let sections = (q || !activeSection)
      ? data.sections
      : data.sections.filter(s => s.id === activeSection);

    // Pipeline:
    //   1. Filter by token-AND match against the normalised per-item search blob.
    //   2. Apply the user-selected sort.
    //   3. (only when search is active) Re-sort by relevance DESC. JS sort is
    //      stable since ES2019, so items with equal relevance preserve the
    //      user-selected order from step 2 — i.e. the user's sort survives as
    //      a tie-breaker within each relevance group.
    return sections.map(s => {
      if (!q) {
        return { ...s, items: sortWineListItems(s.items, sortMode) };
      }
      const filtered = s.items.filter(item =>
        matchesQuery(buildItemSearchText(item, s), q),
      );
      const userSorted = sortWineListItems(filtered, sortMode);
      // Pre-compute the score per item so the comparator is constant-time.
      const scoreById = new Map<string, number>();
      for (const item of userSorted) {
        scoreById.set(item.id, scoreWineListItem(item, s, q));
      }
      const ranked = [...userSorted].sort(
        (a, b) => (scoreById.get(b.id) ?? 0) - (scoreById.get(a.id) ?? 0),
      );
      return { ...s, items: ranked };
    });
  }, [search, activeSection, sortMode, data]);

  const handleSectionChip = (id: string | null) => {
    setActiveSection(id);
    if (id) {
      // Soft-scroll to the section heading
      requestAnimationFrame(() => {
        sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: alpha('#0e0d0c', 0.92),
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* List title row */}
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2.5, md: 5 },
            pt: { xs: 2.5, md: 4 },
            pb: { xs: 2, md: 2.5 },
          }}
        >
          {/* W9 — logo and list title share a single horizontal line.
              Logo to the left, vertically centered, capped at 52px (phone) /
              64px (tablet+). When no logo is set or the image fails to load,
              the small uppercase venue caption renders above the title as
              before. Description always sits beneath the title row. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap' }}>
            {resolvedLogoSrc && !logoFailed ? (
              <Box
                component="img"
                src={resolvedLogoSrc}
                alt={data.list.venueName ?? 'Venue logo'}
                sx={{
                  display: 'block',
                  maxHeight: { xs: 52, md: 64 },
                  maxWidth: { xs: 160, md: 220 },
                  width: 'auto',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
                onError={() => {
                  // W9D — surface load failures so a stuck-on-fallback render
                  // is debuggable. Most common cause is a stale cached 404
                  // (browser cached a missing-logo response from before the
                  // venue uploaded one). The cache-bust on resolvedLogoSrc
                  // should prevent it; if this still fires, check the URL
                  // resolves on the API host.
                  // eslint-disable-next-line no-console
                  console.warn('[W9D] venue logo failed to load:', resolvedLogoSrc);
                  setLogoFailed(true);
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                {data.list.venueName ?? 'Wine List'}
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: SERIF,
                fontSize: { xs: '1.7rem', md: '2.4rem' },
                lineHeight: 1.1,
                color: 'text.primary',
                fontWeight: 500,
                letterSpacing: '-0.015em',
                minWidth: 0,
              }}
            >
              {data.list.name}
            </Typography>
          </Box>
          {data.list.description && (
            <Typography
              sx={{
                mt: 0.75,
                fontSize: { xs: '0.85rem', md: '0.95rem' },
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {data.list.description}
            </Typography>
          )}

          {/* Search + Sort — compact row, search expands to fill, sort sits at the end */}
          <Box sx={{ mt: { xs: 2, md: 2.5 }, display: 'flex', gap: 1, alignItems: 'stretch' }}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search wines, producers, regions…"
              variant="outlined"
              // Wine names / producers are mostly proper nouns and non-dictionary
              // words. Native autocorrect / spellcheck / autocapitalize all hurt
              // search behaviour ("Meursault" gets red-underlined or rewritten on
              // mobile). Turn them off explicitly via the underlying input.
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                },
                htmlInput: {
                  autoCorrect: 'off',
                  autoCapitalize: 'none',
                  spellCheck: false,
                  // type="search" gives the iOS keyboard a Search key and
                  // suppresses some heuristics that fight wine vocabulary.
                  type: 'search',
                  // Web Speech / address-book autofill suggestions also misfire here.
                  autoComplete: 'off',
                  // aria-label so screen readers don't only get the placeholder.
                  'aria-label': 'Search wines',
                },
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha('#fff', 0.025),
                  borderRadius: 99,
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.disabled' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.95rem',
                  py: 1.25,
                },
              }}
            />

            {/* Sort — responsive UX:
                  Phone (xs–sm): single Sort button → popover Menu of all 7 options
                  Tablet (≥md):  visible chip row, direction toggles on repeat tap
                Both presentations write to the same sortMode and the same
                sortWineListItems util — sorting still runs within sections,
                search is unaffected. */}
            {isTablet ? (
              <SortChipRow value={sortMode} onChange={setSortMode} />
            ) : (
              <SortButton value={sortMode} onChange={setSortMode} />
            )}

            {/* W10 — Shortlist (N) entry. Hidden when nothing is shortlisted
                so the header stays clean for first-time browsing. Heart icon
                + count read at a glance on both phone and tablet. */}
            {shortlist.count > 0 && (
              <Button
                size="small"
                onClick={() => setShortlistOpen(true)}
                startIcon={<ShortlistFilledIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                aria-label={`Open shortlist, ${shortlist.count} ${shortlist.count === 1 ? 'wine' : 'wines'}`}
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  borderRadius: 99,
                  height: 40,
                  px: 1.75,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  backgroundColor: alpha('#fff', 0.025),
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.04),
                    borderColor: 'text.disabled',
                  },
                }}
              >
                Shortlist ({shortlist.count})
              </Button>
            )}
          </Box>
        </Box>

        {/* Section nav — sticky chip strip */}
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2.5, md: 5 },
            pb: 1.5,
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <SectionChip
            label="All"
            active={activeSection === null}
            onClick={() => handleSectionChip(null)}
          />
          {data.sections.map(s => (
            <SectionChip
              key={s.id}
              label={s.name}
              active={activeSection === s.id}
              onClick={() => handleSectionChip(s.id ?? null)}
            />
          ))}
        </Box>
      </Box>

      {/* ── Sections list ─────────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2.5, md: 5 },
          pt: { xs: 1.5, md: 3 },
          pb: 8,
        }}
      >
        {/* Search-aware result count.
            Shown only when search is active. The pre-search default is "no
            count" — the section headings already communicate quantity. */}
        {(() => {
          if (!search.trim()) return null;
          const total = visibleSections.reduce((acc, s) => acc + s.items.length, 0);
          let label: string;
          if (total === 0) label = 'No wines found';
          else if (total === 1) label = '1 wine found';
          else label = `${total} wines found`;
          return (
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: 'text.secondary',
                letterSpacing: '0.02em',
                mb: { xs: 1.5, md: 2 },
              }}
              aria-live="polite"
            >
              {label}
            </Typography>
          );
        })()}

        {visibleSections.every(s => s.items.length === 0) ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {search.trim() ? 'No wines found.' : 'No wines to show.'}
            </Typography>
          </Box>
        ) : (
          visibleSections.map(section => (
            section.items.length === 0 ? null : (
              <Box
                key={section.id ?? 'unsectioned'}
                ref={(el: HTMLDivElement | null) => {
                  if (section.id) sectionRefs.current[section.id] = el;
                }}
                sx={{ pt: { xs: 4, md: 6 }, mb: { xs: 2, md: 3 }, scrollMarginTop: 200 }}
              >
                {/* Section heading */}
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                  <Typography
                    sx={{
                      fontFamily: SERIF,
                      fontSize: { xs: '1.5rem', md: '1.85rem' },
                      color: 'text.primary',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {section.name}
                  </Typography>
                  {section.description && (
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: '0.82rem', md: '0.9rem' },
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      {section.description}
                    </Typography>
                  )}
                </Box>

                {/* W9 — single vertical column on every viewport. Easier to
                    scan like a printed wine list than a 2-column grid. */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {section.items.map(item => (
                    <Box
                      key={item.id}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <WineCard
                        item={item}
                        onClick={() => setDetailItem(item)}
                        isShortlisted={shortlist.has(item.id)}
                        onToggleShortlist={shortlist.toggle}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )
          ))
        )}
      </Box>

      {/* ── Detail modal ──────────────────────────────────────────────── */}
      <WineDetail item={detailItem} onClose={() => setDetailItem(null)} fullScreen={!isTablet} />

      {/* W10 — shortlist modal. Tapping a row opens the existing WineDetail
          modal for that wine; the shortlist modal closes so the detail isn't
          stacked behind it. */}
      <ShortlistDialog
        open={shortlistOpen}
        onClose={() => setShortlistOpen(false)}
        items={shortlistItems}
        onRemove={(itemId) => shortlist.toggle(itemId)}
        onClearAll={() => shortlist.clear()}
        onSelectItem={(item) => {
          setShortlistOpen(false);
          setDetailItem(item);
        }}
      />
    </Box>
  );
};

// ── Section chip ──────────────────────────────────────────────────────────────

const SectionChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label, active, onClick,
}) => (
  <Chip
    label={label}
    onClick={onClick}
    sx={{
      flexShrink: 0,
      borderRadius: 99,
      px: 0.5,
      height: 32,
      fontSize: '0.78rem',
      fontWeight: 600,
      letterSpacing: '0.04em',
      backgroundColor: active ? 'primary.main' : 'transparent',
      color: active ? '#1a1816' : 'text.secondary',
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'divider',
      '&:hover': {
        backgroundColor: active ? 'primary.main' : alpha('#fff', 0.04),
      },
    }}
  />
);

// ── Wrap in own ThemeProvider so the prototype is fully self-contained ───────

const PreviewMock: React.FC = () => (
  <ThemeProvider theme={previewTheme}>
    <CssBaseline />
    <WineListMenuView data={MOCK_DATA} />
  </ThemeProvider>
);

export default PreviewMock;
